/**
 * Pure view-model helpers for the Kubera desk (unit-tested in kuberaView.test.js).
 */

const n = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

export const inr = (v, digits = 0) => {
    const x = n(v);
    if (x === null) return '—';
    const s = Math.abs(x).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return x < 0 ? `-₹${s}` : `₹${s}`;
};

export const pts = (v, digits = 2) => {
    const x = n(v);
    return x === null ? '—' : x.toFixed(digits);
};

const BLOCK_LABELS = {
    kill: ['KILLED', 'error'],
    feed_dead: ['FEED DEAD', 'error'],
    feed_stale: ['FEED STALE', 'warning'],
    paused: ['PAUSED', 'warning'],
    job_control_cancel: ['CANCELLED IN INSIGHTS', 'warning'],
    order_reconcile: ['ORDER MISMATCH', 'error'],
    no_session: ['NO KITE SESSION', 'error'],
    redis_unreachable: ['REDIS DOWN', 'warning'],
};

/** Headline status for one book: halts and broker problems first, then blocks, then open / flat. */
export function bookStatus(book = {}) {
    if (book.halted) return { label: 'HALTED', tone: 'error' };
    if ((book.orphaned || []).length) return { label: 'ORPHANED LIVE POSITION', tone: 'error' };
    if (book.blocked) {
        const [label, tone] = BLOCK_LABELS[book.blocked] || [String(book.blocked).replace(/_/g, ' ').toUpperCase(), 'warning'];
        return { label: `${label}${book.position ? ' · OPEN' : ''}`, tone };
    }
    if (book.entriesPaused) return { label: 'ENTRIES PAUSED', tone: 'warning' };
    if (book.position) return { label: 'OPEN', tone: 'success' };
    return { label: 'FLAT', tone: 'default' };
}

/**
 * Where the current spread sits between the take-profit and the stop for this book's own credit.
 * 0 = at take-profit, 1 = at the stop. Tone turns amber at 70%, red at 85%.
 */
export function stopGauge(book = {}) {
    const lv = book.liveView;
    const pos = book.position;
    if (!lv || !pos || n(lv.spread) === null || n(lv.stopLevel) === null) return null;
    const lo = n(lv.tpLevel) ?? 0;
    const hi = n(lv.stopLevel);
    const span = Math.max(1e-9, hi - lo);
    const frac = Math.min(1, Math.max(0, (n(lv.spread) - lo) / span));
    const creditFrac = Math.min(1, Math.max(0, (n(pos.credit) - lo) / span));
    return {
        frac,
        creditFrac,
        tone: frac >= 0.85 ? 'error' : frac >= 0.7 ? 'warning' : 'success',
        text: `spread ${pts(lv.spread)} · stop ${pts(hi)} · take-profit ≤ ${pts(lo)} · credit ${pts(pos.credit)}`,
    };
}

export function positionLine(pos) {
    if (!pos) return null;
    const kind = pos.side === 'PE' ? 'Bull put' : 'Bear call';
    return `${kind} ${pos.sellK}/${pos.buyK} ${pos.expiry} · ${pos.lots} lots (${pos.qty}) · opened ${pos.opened} (${String(pos.branch || '').replace(/_/g, ' ')})`;
}

export function bookMetrics(book = {}) {
    const pos = book.position;
    const lv = book.liveView || {};
    const metrics = [];
    if (pos) {
        metrics.push(
            { label: 'Credit', value: pts(pos.credit), detail: `sold ${pts(pos.sellPx)} / bought ${pts(pos.buyPx)}` },
            { label: 'Spread now', value: pts(lv.spread), detail: lv.worstSpread != null ? `worst ${pts(lv.worstSpread)}` : null,
                tone: n(lv.stopDistance) !== null && n(lv.stopDistance) <= 0 ? 'error' : 'default' },
            { label: 'Stop at', value: pts(lv.stopLevel), detail: lv.stopDistance != null ? `${pts(lv.stopDistance)} away` : null },
            { label: 'MTM', value: inr(lv.mtm), tone: n(lv.mtm) > 0 ? 'success' : n(lv.mtm) < 0 ? 'error' : 'default' },
        );
    }
    metrics.push(
        { label: book.live ? 'Kubera P&L (account)' : 'Corpus', value: inr(book.corpus) },
        { label: 'Next entry size', value: book.nextLots != null ? `${book.nextLots} lots` : '—',
            detail: book.sizing ? `base ${book.sizing.base_lots} lots since ${book.sizing.since}` : null },
    );
    return metrics;
}

const dir = (d) => (Number(d) === 1 ? 'up' : Number(d) === -1 ? 'down' : '?');

/** One-line explanation of a decision run with the indicators it used. */
export function decisionText(d) {
    if (!d) return 'No decision run yet today (first run 10:15).';
    const parts = [];
    if (d.stDir != null) parts.push(`Supertrend ${dir(d.stDir)}${d.stDirPrev != null && d.stDirPrev !== d.stDir ? ` (flipped from ${dir(d.stDirPrev)})` : ''}`);
    if (d.macdHist != null) parts.push(`MACD hist ${pts(d.macdHist)}${d.macdHistPrev != null ? ` (prev ${pts(d.macdHistPrev)})` : ''}`);
    if (d.rsi != null) parts.push(`RSI ${pts(d.rsi, 1)}`);
    if (d.close != null && d.ema200 != null) parts.push(`close ${pts(d.close, 1)} vs EMA200 ${pts(d.ema200, 1)}`);
    if (d.vix != null) parts.push(`VIX ${pts(d.vix)}`);
    return parts.join(' · ');
}

export function actionLabel(d) {
    if (!d) return { label: '—', tone: 'default' };
    const a = String(d.action || '');
    if (a.includes('entry_skipped')) return { label: `SKIPPED${d.why ? `: ${d.why}` : ''}`, tone: 'warning' };
    if (a.includes('entry')) return { label: a.replace(/_/g, ' ').toUpperCase(), tone: 'success' };
    if (a.startsWith('exit')) return { label: a.replace('exit:', 'EXIT ').toUpperCase(), tone: 'warning' };
    if (a === 'halted') return { label: 'HALTED', tone: 'error' };
    if (a === 'no_technicals') return { label: 'NO BARS', tone: 'warning' };
    return { label: a.toUpperCase() || '—', tone: 'default' };
}

const QUIET = new Set(['prearm', 'day_start', 'reconcile', 'reconcile_orders']);

/** Ops events worth showing, newest first, as {at, tone, text}. */
export function eventRows(events = []) {
    const out = [];
    events.forEach((e) => {
        const p = e.payload || {};
        const who = e.book ? `[${e.book}] ` : '';
        let text = null;
        let tone = 'default';
        switch (e.kind) {
        case 'boot': text = `Service started (${p.mode}, prime ${p.prime || '?'}, config ${p.config_hash || '?'}, ${p.git_sha ? String(p.git_sha).slice(0, 8) : 'unknown build'})`; break;
        case 'preflight': {
            const bad = (p.checks || []).filter((c) => !c.ok);
            text = p.passed ? `Pre-flight passed${bad.length ? ` with ${bad.length} warning(s): ${bad.map((c) => c.name).join(', ')}` : ''}` : `Pre-flight FAILED: ${bad.filter((c) => c.fatal).map((c) => `${c.name} (${c.detail})`).join('; ')}`;
            tone = p.passed ? (bad.length ? 'warning' : 'success') : 'error';
            break;
        }
        case 'halt': text = `${who}HALTED: ${p.why}`; tone = 'error'; break;
        case 'entry_skipped': text = `${who}entry skipped: ${p.why}`; tone = 'warning'; break;
        case 'entry_failed': text = `${who}entry failed${p.hedge_broken ? ' (HEDGE BROKEN)' : ''}: ${p.error}`; tone = 'error'; break;
        case 'entry_incident': text = `${who}partial entry: ${p.incident}`; tone = 'error'; break;
        case 'feed_rest_fallback': text = `Feed: ${(p.reasons || []).join('; ')}`; tone = 'warning'; break;
        case 'feed_dead': text = `FEED DEAD: ${(p.reasons || []).join('; ')}`; tone = 'error'; break;
        case 'feed_recovered': text = 'Feed recovered'; tone = 'success'; break;
        case 'kill': text = `Kill switch active (${p.why})`; tone = 'error'; break;
        case 'pause': text = `Paused books: ${(p.paused || []).join(', ') || 'none'}`; tone = 'warning'; break;
        case 'control': text = `${p.action}${p.book ? ` ${p.book}` : ''} from Insights${p.note ? `: ${p.note}` : ''}`; tone = p.action === 'kill' ? 'error' : 'warning'; break;
        case 'job_control': text = `${who}ca_job_control ${p.action}`; tone = 'warning'; break;
        case 'sizing_rebase': text = `${who}sizing base ${p.base_lots} lots (next entry ${p.lots_now} lots)`; break;
        case 'orphans': text = `${who}ORPHANED live position(s): ${JSON.stringify(p.orphans)}`; tone = 'error'; break;
        case 'orphans_cleared': text = `${who}orphaned positions cleared (flat at broker)`; tone = 'success'; break;
        case 'expiry_margin': text = `${who}expiry-day margin ${p.ok ? 'OK' : 'SHORTFALL'}: need ${inr(p.need_rs)}, available ${inr(p.available_rs)}`; tone = p.ok ? 'success' : 'error'; break;
        case 'expiry_moved': text = `${who}exchange moved expiry ${p.from} → ${p.to}`; tone = 'warning'; break;
        case 'bars_missing': text = `${p.run} run skipped: ${p.why}`; tone = 'warning'; break;
        case 'bar_mismatch': text = `Kite ${p.bar} bar differs from md_norm ticks (close Δ ${p.close_diff})`; tone = 'warning'; break;
        case 'run_skipped': text = `${p.run} run skipped (${p.why})`; tone = 'warning'; break;
        case 'tick_check_failed': text = 'No md_norm ticks by 09:16'; tone = 'error'; break;
        case 'mode_switch': text = `${who}mode switch ${p.from} → ${p.to}`; tone = 'warning'; break;
        case 'alert_critical': text = `CRITICAL alert sent${p.paged ? ' (paged)' : ''}: ${p.msg}`; tone = 'error'; break;
        case 'alert_call': text = `Phone call placed (${p.why})`; tone = 'error'; break;
        case 'alert_ack': text = 'Critical alert acknowledged'; tone = 'success'; break;
        case 'redis_error': text = `Redis unreachable (${p.error}): entries blocked`; tone = 'warning'; break;
        default:
            if (QUIET.has(e.kind)) {
                if ((e.kind === 'reconcile_orders' || e.kind === 'reconcile') && (p.diffs || []).length) {
                    text = `${who}${e.kind === 'reconcile_orders' ? 'ORDER' : 'position'} reconciliation diffs: ${JSON.stringify(p.diffs)}`;
                    tone = e.kind === 'reconcile_orders' ? 'error' : 'warning';
                }
            } else {
                text = `${who}${e.kind}`;
            }
        }
        if (text) out.push({ at: e.at, tone, text, kind: e.kind });
    });
    return out;
}

/** Service heartbeat from the snapshot age. */
export function serviceStatus(service = {}) {
    if (!service.running) return { label: 'SERVICE STOPPED', tone: 'default' };
    if (service.snapshotAgeS != null && service.snapshotAgeS > 30) return { label: `STALE ${service.snapshotAgeS}s`, tone: 'warning' };
    return { label: 'SERVICE RUNNING', tone: 'success' };
}

export function feedStatus(feed) {
    if (!feed) return { label: 'FEED ?', tone: 'default' };
    if (feed.dead) return { label: 'FEED DEAD', tone: 'error' };
    return [{ label: 'FEED OK', tone: 'success' }, { label: 'FEED STALE', tone: 'warning' },
        { label: 'FEED REST', tone: 'warning' }, { label: 'FEED DEAD', tone: 'error' }][feed.level] || { label: 'FEED ?', tone: 'default' };
}

/** Today's runs grouped by time: [{time, rows: [{book, action, why}]}] (latest first). */
export function runsByTime(decisions = []) {
    const map = new Map();
    decisions.forEach((d) => {
        if (!map.has(d.time)) map.set(d.time, []);
        map.get(d.time).push(d);
    });
    return Array.from(map.entries()).map(([time, rows]) => ({ time, rows })).reverse();
}

/** Points for an equity sparkline: equity (corpus + MTM) per day. */
export function sparkPoints(points = [], W = 260, H = 48) {
    const vals = points.map((p) => n(p.equity)).filter((v) => v !== null);
    if (vals.length < 2) return null;
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const span = hi - lo || 1;
    const step = W / (vals.length - 1);
    const d = vals.map((v, i) => `${i ? 'L' : 'M'}${(i * step).toFixed(1)},${(H - 2 - ((v - lo) / span) * (H - 4)).toFixed(1)}`).join(' ');
    return { d, first: vals[0], last: vals[vals.length - 1], lo, hi };
}
